// @flow

// Modules
import React from 'react';
import styled, { css } from 'styled-components';
import { debounce, findIndex, has } from 'lodash';

// Methods
import { changeLanguageL10n } from 'src/methods/l10n';
import WsPushClient from 'src/methods/network/wspush';

// Styles
import { BLACK } from 'src/styles/palettes';
import { getFontSize } from 'src/styles/basestyle';

// Components
import Popup from 'components/popup';
import Header from 'containers/header';
import InfosPlayer from 'containers/infosplayer';
import List from 'containers/playerslist';

/**
 * Styles
 */

const Challenges = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: ${__BUILD_MODE__ === 'production' ? '100%' : '100vh'};
  padding: 2%;
  font-family: ChallYantramanav;
  background-color: ${BLACK};
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 40%;
  ${(props) => !props.isMobile && 'min-width: 32.5em;'}
  width: 32.5em;
  margin-right: 1.3em;

  ${(props) =>
    props.isColumnDisplay &&
    css`
      flex: 1;
      max-width: 700px;
      margin-right: 0px;
    `}
  }
`;

/**
 * Types
 */

type HeaderParamsType = {
  challenge?: ?string,
  limit?: ?string,
  date?: ?string,
  ts: ?any
};

type MemoType = {
  selectedChallenge: number,
  selectedType: number,
  selectedDate: number
};

export type PropTypes = {
  applicationName: string,
  headerParams?: ?HeaderParamsType,
  headerMemo?: ?MemoType,
  language: string,
  operator: string,
  env: string,
  isMobile?: boolean,
  hidePlayerInfo?: boolean,
  refreshTokenEndpoint?: string,
  getToken?: () => Promise<Object>,
  navigate: (action: string) => void,
  setMemo?: (headerMemo: MemoType) => void
};

type StateTypes = {
  challenges: Object,
  selectedChallenge: number,
  selectedType: number,
  selectedDate: number,
  playersList: ?Array<Object>,
  selectedPlayer: number,
  isColumnDisplay: boolean,
  isLoading: boolean,
  isInit: boolean,
  hasPopup: boolean
};

/*
 ** Constants
 */

const COLUMN_WIDTH_CAP = getFontSize() * 63;

/*
 ** Component
 */

class Home extends React.Component<PropTypes, StateTypes> {
  rootElement: any = React.createRef();
  tokenTimeout: any = null;

  constructor(props: PropTypes) {
    super();

    const { headerMemo } = props;

    this.state = {
      // Initial configurations of all challenges, ranges and periods
      challenges: null,
      // Id of the challenge selected
      selectedChallenge: (!!headerMemo && headerMemo.selectedChallenge) || 0,
      // Id of the range selected
      selectedType: (!!headerMemo && headerMemo.selectedType) || 0,
      // Id of the period selected
      selectedDate: (!!headerMemo && headerMemo.selectedDate) || 0,
      // Lists of players for selected infos
      playersList: null,
      // Id of the selected player in list
      selectedPlayer: 0,
      // Has received datas
      isLoading: true,
      // Dissociate first conf request from the others
      isInit: true,
      // Has list in Column
      isColumnDisplay: false,
      // Is popup open ?
      hasPopup: false
    };
  }

  // Connect socket
  initSocket() {
    const { env, operator, language, getToken, refreshTokenEndpoint, headerParams } = this.props;
    const {
      socketConnection,
      getConfiguration,
      generateToken,
      setConnected,
      setAuthenticated,
      getUrlLimitTranslation
    } = WsPushClient;
    const { ids, authenticated, connected } = WsPushClient.getInfos();

    socketConnection(env, operator, (data) => {
      const id = data ? data.id : null;

      // Datas msg
      if (id) {
        // Config msg
        if (id === ids.configuration) {
          if (headerParams && this.state.isInit) {
            const { challenge, limit } = headerParams;

            if (challenge && limit) {
              getUrlLimitTranslation(challenge, limit);
            } else if (challenge) {
              this.setHeaderParams(data.challenges);
            }
          }

          this.setState({ challenges: data.challenges, isInit: false });
        }

        // Web url/type tranlastions msg
        if (id === ids.limitTranslation) {
          this.setHeaderParams(this.state.challenges, data.data.challenge_id);
        }

        // Challenge Ranking msg
        if (id === ids.challenge) {
          const playersList = data.data;
          const selectedPlayer = findIndex(playersList, (player) => player.is_hero);

          this.setState({
            playersList,
            selectedPlayer: selectedPlayer !== -1 ? selectedPlayer : 0,
            isLoading: false
          });
        }
      }

      // Connection msg
      if (has(data, 'connected')) {
        setConnected(data.connected);

        if (data.connected && !connected) {
          getConfiguration(language, operator);
          generateToken(getToken, refreshTokenEndpoint);
        }
      }

      // Authentication msg
      if (has(data, 'authenticated')) {
        setAuthenticated(data.authenticated);

        if (data.authenticated && !authenticated) {
          this.getPlayersList();
        } else if (!data.authenticated && authenticated) {
          generateToken(getToken, refreshTokenEndpoint);
        }
      }

      // Error msg
      if (has(data, 'error')) {
        if (connected) {
          this.setState({ playersList: null, isLoading: false });
        } else {
          // CONF ERROR
        }
      }
    });
  }

  // Launch challenges on a specific ranking
  setHeaderParams(challenges: Object, translatedLimit?: string) {
    const { headerParams, setMemo } = this.props;
    const { selectedChallenge, selectedType, selectedDate } = this.state;
    const CHALLENGE_URL_ID = { expresso: 'EXP', cashgame: 'CG', sitandgo: 'SNG', grandprix: 'GP', pm: 'PM' };
    const checkId = (id) => (id !== -1 ? id : 0);
    const challengeId = checkId(
      findIndex(
        challenges,
        (challenge) =>
          headerParams && headerParams.challenge && challenge.id === CHALLENGE_URL_ID[headerParams.challenge]
      )
    );

    let limitId = selectedType;
    let dateId = 0;

    if (translatedLimit) {
      let limits = challenges && challenges[challengeId] && challenges[challengeId].types;
      limitId = checkId(findIndex(limits, (limit) => limit.id === translatedLimit));

      if (headerParams && headerParams.date) {
        const dates = limits[limitId] && limits[limitId].date_range.valid_dates;
        let splittedDate = headerParams.date.split('-');
        let timestamp = new Date(
          parseInt(splittedDate[2]),
          parseInt(splittedDate[1]) - 1,
          parseInt(splittedDate[0])
        ).getTime();

        dateId = checkId(findIndex(dates, (date) => headerParams && date === timestamp / 1000));
      }

      // Get player list if no change (Because no modifications does not activate listeners)
      if (selectedChallenge === challengeId && selectedType === limitId && selectedDate === dateId) {
        this.getPlayersList();
      } else {
        this.setState({ selectedChallenge: challengeId, selectedType: limitId, selectedDate: dateId });
      }
    } else {
      // Get player list if no change (Because no modifications does not activate listeners)
      if (selectedChallenge === challengeId) {
        this.getPlayersList();
      } else {
        this.setState({ selectedChallenge: challengeId, selectedType: 0, selectedDate: 0 });
      }
    }

    !!setMemo &&
      setMemo({
        selectedChallenge: challengeId,
        selectedType: limitId,
        selectedDate: dateId || selectedDate
      });
  }

  // Determine column/row display for list depending on page size
  setDisplayMode: () => void = () => {
    const { state, rootElement } = this;
    const { isColumnDisplay } = state;

    if (rootElement) {
      const windowWidth = rootElement.getBoundingClientRect().width;
      const isMinSize = windowWidth < COLUMN_WIDTH_CAP;

      if ((isMinSize && !isColumnDisplay) || (!isMinSize && isColumnDisplay)) {
        this.setState((prev) => ({ isColumnDisplay: isMinSize }));
      }
    }
  };

  componentDidMount(): () => void {
    const onResize = debounce(this.setDisplayMode.bind(this), 100);

    this.initSocket();

    // Set initial column/row display mode and add resize listener
    this.setDisplayMode();
    window.addEventListener('resize', onResize);

    // Set initial language
    changeLanguageL10n(this.props.language || 'en_US');

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }

  componentDidUpdate(prevProps: PropTypes, prevState: StateTypes) {
    const { isColumnDisplay, isInit } = this.state;
    const { headerParams } = this.props;
    const isModified = (key: string) => this.state[key] !== prevState[key];

    if (isModified('selectedChallenge') || isModified('selectedType') || isModified('selectedDate')) {
      // Reinit all if challenge is changed
      this.setState({ selectedPlayer: 0, playersList: null, isLoading: true });
      this.getPlayersList();
    }

    // Ask initial challenge when configuration is received
    if (isModified('challenges') && !(this.props.headerParams && isInit)) {
      this.getPlayersList();
    }

    // Close popup if display change
    if (isModified('isColumnDisplay') && !isColumnDisplay) {
      this.setState({ hasPopup: false });
    }

    // Check if headerParams is Modified
    if (headerParams && headerParams !== prevProps.headerParams) {
      const { challenge, limit } = headerParams;

      if (challenge && limit) {
        WsPushClient.getUrlLimitTranslation(challenge, limit);
      }
    }

    if (prevProps.language !== this.props.language) {
      changeLanguageL10n(this.props.language);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.tokenTimeout);
    WsPushClient.disconnect();
  }

  getPlayersList() {
    const { challenges, selectedChallenge, selectedType, selectedDate } = this.state;
    const actualChallenge = challenges && challenges[selectedChallenge];
    const types = actualChallenge && actualChallenge.types;
    const actualType = types && types[selectedType];
    const actualDate = actualType && actualType.date_range.valid_dates[selectedDate];
    const operation_name = actualType && actualType.operation_name;

    // Get selected challenge infos
    if (challenges && actualType) {
      WsPushClient.getSpecificChallenge(operation_name, actualType.id, actualDate);
    }
  }

  setInfosMemo(key: string, value: number) {
    const { headerMemo, setMemo } = this.props;
    const { selectedChallenge, selectedType, selectedDate } = this.state;

    if (key === 'selectedChallenge' && value !== selectedChallenge) {
      const newValues = { selectedChallenge: value, selectedType: 0, selectedDate: 0 };

      this.setState(newValues);
      !!setMemo && setMemo(newValues);
    } else if (key === 'selectedType' && value !== selectedType) {
      this.setState({ selectedType: value, selectedDate: 0 });

      !!setMemo && setMemo({ ...headerMemo, selectedType: value, selectedDate: 0 });
    } else if (key === 'selectedDate' && value !== selectedDate) {
      this.setState({ selectedDate: value });

      !!setMemo && setMemo({ ...headerMemo, selectedDate: value });
    }
  }

  resetConfiguration: () => void = () => {
    const { language, operator } = this.props;

    this.setState({ selectedPlayer: 0, playersList: null, isLoading: true });
    WsPushClient.getConfiguration(language, operator);
  };

  renderInfosPlayer(): null | React$Element<any> {
    const { challenges, playersList, selectedChallenge, selectedType, selectedPlayer } = this.state;
    const actualChallenge = challenges && challenges[selectedChallenge];

    if (this.props.hidePlayerInfo) {
      return null;
    }

    return (
      <InfosPlayer
        challenge={actualChallenge}
        selectedType={selectedType}
        selectedPlayer={selectedPlayer}
        playersList={playersList}
      />
    );
  }

  renderList(): React$Element<any> {
    const { isMobile } = this.props;
    const { challenges, playersList, selectedChallenge, selectedPlayer, isLoading, isColumnDisplay } = this.state;
    const handleListClick = (playerId: number) => {
      const { isMobile } = this.props;
      const { isColumnDisplay } = this.state;

      this.setState({ selectedPlayer: playerId, hasPopup: isMobile || isColumnDisplay });
    };
    const selectedChallengeId = challenges && challenges[selectedChallenge] && challenges[selectedChallenge].id;

    return (
      <List
        playersList={playersList}
        selectedPlayer={selectedPlayer}
        isMobile={!!isMobile}
        isLoading={!challenges || isLoading}
        isColumnDisplay={isColumnDisplay}
        selectedChallengeId={selectedChallengeId}
        onItemClick={handleListClick}
      />
    );
  }

  render(): React$Element<any> {
    const { challenges, selectedChallenge, selectedType, selectedDate, isColumnDisplay, hasPopup } = this.state;
    const { isMobile, navigate, hidePlayerInfo } = this.props;

    const setSelectedChallenge = (id: number) => this.setInfosMemo('selectedChallenge', id);
    const setSelectedType = (id: number) => this.setInfosMemo('selectedType', id);
    const setSelectedDate = (id: number) => this.setInfosMemo('selectedDate', id);

    return (
      <Challenges ref={(ref) => (this.rootElement = ref)}>
        <LeftColumn isColumnDisplay={isColumnDisplay} isMobile={!!isMobile}>
          <Header
            challenges={challenges}
            selectedChallenge={selectedChallenge}
            setSelectedChallenge={setSelectedChallenge}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            resetConf={this.resetConfiguration.bind(this)}
            navigate={navigate}
          />
          {!isMobile && !isColumnDisplay && this.renderInfosPlayer()}
          {isColumnDisplay && this.renderList()}
        </LeftColumn>
        {!isColumnDisplay && this.renderList()}

        {hasPopup && !hidePlayerInfo && (
          <Popup onHide={() => this.setState({ hasPopup: false })}>{this.renderInfosPlayer()}</Popup>
        )}
      </Challenges>
    );
  }
}

export default Home;
